import type { FC } from 'react';
import React, { memo, useCallback, useEffect } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import {
	SPACE_OVERVIEW_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
	VIEW_ALL_MACRO_EXPERIENCE,
	FRONT_COVER_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { routeNameToContentTypeMap } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import {
	getExperienceTracker,
	stopExperienceOnError,
} from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { usePageState } from '@confluence/page-context';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ContentContainer } from '@confluence/content-container/entry-points/ContentContainer.ts';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT } from '@confluence/content-header/entry-points/defaultQueryVariables.js';
import {
	ContentBlogUnifiedQueryArtifact,
	ContentUnifiedQueryArtifact,
	type ContentBlogUnifiedQueryType,
	type ContentBlogUnifiedQueryVariablesType,
	type ContentUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
} from '@confluence/content-unified-query';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper.ts';
import { getProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { PagePrerequisites } from '@confluence/content-prerequisites/entry-points/PagePrerequisites.ts';
import { useDocumentUpdateStatusDispatch } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import {
	VIEW_PAGE,
	SPACE_OVERVIEW,
	COMPANY_HUB,
} from '@confluence/named-routes/entry-points/routes.ts';
import { RENDERER } from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import { ContentStateContainer } from '@confluence/content-state/entry-points/useContentState.ts';
import {
	ReloadType,
	ReloadPriority,
	useQuickReloadSubscriber,
} from '@confluence/quick-reload/entry-points/subscription.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { useUnreadCommentsSetup } from '@confluence/unread-comments/entry-points/useUnreadCommentsSetup.ts';

import type { BaseViewPageProps } from './BaseViewPageProps.ts';
import { VIEW_PAGE_CONTAINER_METRIC } from './perf.config.tsx';
import { useContentExtensionMetrics } from './useContentExtensionMetrics.ts';
import { useContentReloadOnFabricSupportChange } from './useContentReloadOnFabricSupportChange.ts';

const pageMap = {
	SPACE_OVERVIEW: {
		route: SPACE_OVERVIEW,
		errorAttribution: 'next-space-overview',
		name: SPACE_OVERVIEW_EXPERIENCE,
		isContentView: false,
	},
	COMPANY_HUB: {
		route: COMPANY_HUB,
		errorAttribution: 'next-company-hub',
		name: FRONT_COVER_LOAD_EXPERIENCE,
		isContentView: false,
	},
	page: {
		route: VIEW_PAGE,
		errorAttribution: 'next-view-page',
		name: VIEW_PAGE_EXPERIENCE,
		isContentView: true,
	},
};

interface ViewPageContainerProps extends BaseViewPageProps {
	children?: (props: {
		isThemed: boolean;
		contentQueryData: ContentUnifiedQueryType | ContentBlogUnifiedQueryType | undefined;
		contentQueryError: ApolloErrorInterop | undefined;
	}) => React.ReactNode | null;
	stickyHeader?: React.ReactNode;
	contentScreenStyles?: React.CSSProperties;
	isContainerContentView?: boolean;
	versionOverride?: number | null;
	customPerfData?: Record<string, string | boolean | object>;
	isEmbeddedPage?: boolean;
}

export const ViewPageContainer: FC<ViewPageContainerProps> = memo(
	({
		contentId,
		spaceKey,
		children,
		stickyHeader,
		contentScreenStyles,
		isContainerContentView = true,
		versionOverride = null,
		customPerfData,
		isEmbeddedPage,
	}) => {
		const { setCurrentDocumentVersion, setDocumentNeedsUpdate, setPublishedDocumentVersion } =
			useDocumentUpdateStatusDispatch();
		const [{ routeName = '', contentType }] = usePageState();
		const { edition, userId } = useSessionData();
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

		useUnreadCommentsSetup({
			pageId: contentId,
			pageContentTypeProps: {
				pageMode: 'view',
			},
		});

		const extractDataAndSetPublishedVersion = useCallback(
			({ refetchContentQueryResult }) => {
				const { data } = refetchContentQueryResult;
				if (data) {
					const versionNumber = data.content?.nodes?.[0]?.version?.number;
					if (versionNumber !== undefined && versionNumber !== null) {
						setPublishedDocumentVersion(versionNumber);
					}
				}
			},
			[setPublishedDocumentVersion],
		);

		const isBlogpost = contentType === 'blogpost';
		const skipContentQuery = !contentId || !spaceKey;

		const pageContentQueryResult = useQueryInterop<
			ContentUnifiedQueryType,
			ContentUnifiedQueryVariablesType
		>(ContentUnifiedQueryArtifact, {
			fetchPolicy: 'cache-first',
			context: { single: true },
			skip: skipContentQuery || isBlogpost,
			variables: {
				contentId,
				spaceKey,
				isSSR,
				versionOverride,
				useNewContentTopper: true,
				// confluence-feature-gating/inline-usage: expValEquals must be inlined
				// Only spread agentSessionsPropertyKey/fetchAgentSessionsMarker when the
				// experiment is enabled. When omitted, GraphQL defaults kick in (key="",
				// fetch=false), and — crucially — the query variables object is identical
				// to the pre-change shape, so contract-test mock backends (playwright) still
				// match the stored query hash and don't break.
				...(!!userId && expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
					? {
							agentSessionsPropertyKey: `cc-agent-mention-sessions:${userId}`,
							fetchAgentSessionsMarker: true,
						}
					: {}),
				// Only spread includeAgentTrustReceiptData when the gate is on, so the variables
				// object (and therefore the contract-test query hash) is unchanged when it's off.
				// This must match the value the SSR preloader (preloadContent) writes the
				// ContentUnifiedQuery cache entry with, or the homepage re-fetches on first render.
				...(fg('cc_sparks_agent_trust_receipt') ? { includeAgentTrustReceiptData: true } : {}),
				...(fg('confluence_vp_title_byline_frag_dedup')
					? {
							includeBylineContributors: true,
							bylineContributorsLimit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
						}
					: {}),
				...(fg('confluence_vp_meta_frag_reader') ? { preloadLabels: true } : {}),
			},
		});

		const blogContentQueryResult = useQueryInterop<
			ContentBlogUnifiedQueryType,
			ContentBlogUnifiedQueryVariablesType
		>(ContentBlogUnifiedQueryArtifact, {
			fetchPolicy: 'cache-first',
			context: { single: true },
			skip: skipContentQuery || !isBlogpost,
			variables: {
				contentId,
				spaceKey,
				isSSR,
				versionOverride,
				useNewContentTopper: true,
				...(fg('cc_sparks_agent_trust_receipt') ? { includeAgentTrustReceiptData: true } : {}),
				...(fg('confluence_vp_title_byline_frag_dedup')
					? {
							includeBylineContributors: true,
							bylineContributorsLimit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
						}
					: {}),
				...(fg('confluence_vp_meta_frag_reader') ? { preloadLabels: true } : {}),
			},
		});

		const contentQueryResult = isBlogpost ? blogContentQueryResult : pageContentQueryResult;

		const { route, errorAttribution, name, isContentView } = pageMap[routeName] ?? pageMap['page'];

		const { refetch } = contentQueryResult;

		useContentExtensionMetrics(
			contentQueryResult?.data?.content?.nodes?.[0]?.body?.dynamic?.value,
			customPerfData,
		);

		useEffect(() => {
			// If navigating to a different page, always assume document is up to date.
			// Added for COMMENTS-5327, so when user jumps from an outdated page to another
			// up-to-date page, we won't disable the comment button due to outdated-ness.
			// Caveat: The destination page may not actually be up-to-date, e.g. if the
			// page content was loaded from cache that's saved from the user's previous
			// visit in the same browser tab, and a new version was published since then.
			// Invoking `setDocumentNeedsUpdate(false)` here means `documentNeedsUpdate`
			// may be `false` when page is actually outdated, but this scenario can already
			// occur before this change. COMMENTS-5816 tracks fixing this scenario altogether.
			if (!expValEquals('confluence_fe_disable_comment_if_page_outdated_v2', 'isEnabled', true)) {
				// With confluence_fe_disable_comment_if_page_outdated_v2 ON,
				// `documentNeedsUpdate` is set to true whenever we set a new
				// `publishedDocumentVersion` IF it's different from `currentDocumentVersion`.
				// This useEffect can be cleaned up after v2 is fully rolled out.
				setDocumentNeedsUpdate(false);
			}
		}, [contentId, setDocumentNeedsUpdate]);

		// Use this instead of `contentQueryResult.data?.content?.nodes?.[0]?.version?.number`
		// because that field is fetched by other queries too, and thus may become mismatched
		// with the actual version of `body` in GQL cache.
		const contentVersion =
			contentQueryResult.data?.content?.nodes?.[0]?.body?.dynamic?.content?.version?.number;

		useEffect(() => {
			if (
				contentVersion &&
				expValEquals('confluence_fe_disable_comment_if_page_outdated_v2', 'isEnabled', true)
			) {
				setCurrentDocumentVersion(contentVersion);
			}
		}, [contentVersion, setCurrentDocumentVersion]);

		const reload = useCallback(
			async () => {
				getExperienceTracker().start({
					name,
					timeout: ExperienceTimeout.PAGE_LOAD,
					attributes: {
						contentType: routeNameToContentTypeMap[routeName],
						contentId,
						isSpaceOverviewNextRendered: true,
						edition,
						quickReload: true, //tell exp metrics error came from reload and not primary experience
					},
				});

				try {
					const refetchContentQueryResult = await refetch({
						contentId,
						spaceKey,
						isSSR,
						versionOverride,
						useNewContentTopper: true,
						...(fg('confluence_vp_meta_frag_reader') ? { preloadLabels: true } : {}),
					});

					extractDataAndSetPublishedVersion({ refetchContentQueryResult });
				} catch (error) {
					if (isUnauthorizedError(error) || isExpectedApolloError(error)) {
						markErrorAsHandled(error);
						getExperienceTracker().abort({
							name,
							reason: 'Expected Apollo error',
						});
						return; //if it's an expected GQL failure don't throw or fail the experience
					}
					stopExperienceOnError(name, error);
					throw error; //this successfully avoids the success below
				}
				getExperienceTracker().succeed({ name });
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[refetch, contentId],
		);

		const content = contentQueryResult.data?.content?.nodes?.[0];
		const fabricEditorSupported = contentQueryResult.loading
			? null
			: (content?.metadata?.frontend?.fabricEditorSupported ?? null);
		const bodyRepresentation = contentQueryResult.loading
			? null
			: (content?.body?.dynamic?.representation ?? null);
		useContentReloadOnFabricSupportChange({
			contentId,
			fabricEditorSupported,
			bodyRepresentation,
			reload,
		});

		useQuickReloadSubscriber({
			name: 'content',
			types: [ReloadType.content, ReloadType.comment],
			reload,
		});

		useQuickReloadSubscriber({
			name: 'commentsDataManager',
			types: [ReloadType.comment],
			reload: async () => {
				await getProcessedCommentsDataState().refetch?.();
			},
			priority: ReloadPriority.background,
		});

		return (
			<>
				<PageSegmentLoadStart key={contentId} metric={VIEW_PAGE_CONTAINER_METRIC} />
				<MacroExperienceStart
					contentId={contentId}
					mode={RENDERER}
					name={VIEW_ALL_MACRO_EXPERIENCE}
					timeout={ExperienceTimeout.MACRO_LOAD}
				/>
				<PagePrerequisites
					route={route}
					contentQueryResult={contentQueryResult}
					versionOverride={versionOverride}
				>
					<ContentStateContainer scope={contentId}>
						<ContentContainer
							fetchTheme
							contentThemeSpaceData={
								contentQueryResult.data ? (contentQueryResult.data.space ?? null) : undefined
							}
							contentId={contentId}
							spaceKey={spaceKey}
							key="content-screen"
							errorAttribution={errorAttribution}
							isScreenContentView={isContentView}
							isContainerContentView={isContainerContentView}
							contentScreenStyles={contentScreenStyles}
							stickyHeader={stickyHeader}
							versionOverride={versionOverride}
							isEmbeddedPage={isEmbeddedPage}
						>
							{({ isThemed }) => (
								<article>
									{children &&
										children({
											isThemed,
											contentQueryData: contentQueryResult.data,
											contentQueryError: contentQueryResult.error,
										})}
									<PageSegmentLoadEnd
										key={contentId}
										metric={VIEW_PAGE_CONTAINER_METRIC}
										customData={{ isThemed }}
									/>
								</article>
							)}
						</ContentContainer>
					</ContentStateContainer>
				</PagePrerequisites>
			</>
		);
	},
);
